import React, { memo } from 'react';
import Loading from '@ifit/mp-loading';
import { showFullScreenError } from '../../../../store/actions/error';

import MyListRow from '../row-types/my-list-row';
import QuickFindsRow from '../row-types/quick-finds-row';
import ChallengesRow from '../row-types/challenges-row';
import IncludedWorkoutsRow from '../row-types/included-workouts-row';
import CategoriesRow from '../row-types/categories-row';
import QueryRow from '../row-types/query-row';
import { useGetRowsQuery } from '../../../../client/query/rows';
import { useEquipmentType } from '../../../../client/query/equipment-type';
import { IRow } from '@ifit/gateway-wolf-athenaeum-service';
import { useMembership } from '../../../../client/query/users';
import { useDispatch } from 'react-redux';
import { useFeatureFlags } from '../../../../helpers/good-cop-helper';

const Rows: React.FC = () => {
  const dispatch = useDispatch();
  const { equipmentType } = useEquipmentType();
  const { data, isError, isLoading } = useGetRowsQuery();
  const { membershipType: subscriptionType } = useMembership();
  const featureFlags = useFeatureFlags(['featureflag://active-pulse']);

  const filterRows = (rows: IRow[]): IRow[] => {
    if (!equipmentType) {
      return [];
    }

    let filteredResults = rows.filter(row => {
      const falsyFiltered = row.requiredFeatureFlags.filter(f => Boolean(f));

      return (
        !falsyFiltered.length ||
        falsyFiltered.find(featureFlag => {
          const value = featureFlags[`featureflag://${featureFlag}`];
          if (typeof value === 'string') {
            return equipmentType
              ? value.includes(equipmentType.toLowerCase())
              : false;
          }
          return !!value;
        })
      );
    });

    filteredResults = filteredResults.filter(
      row =>
        !row.subscriptionTypes.length ||
        row.subscriptionTypes.includes(subscriptionType),
    );

    return filteredResults;
  };

  const renderRows = (rows: IRow[]): JSX.Element[] | null => {
    return filterRows(rows).map((item, index) => {
      const {
        type,
        title,
        translatedTitle,
        href,
        query,
        minCompletions,
        maxCompletions,
      } = item;

      switch (type) {
        case 'MyList':
          return (
            <MyListRow
              key={index}
              title={translatedTitle}
              href={href}
              index={index}
            />
          );
        case 'QuickFinds':
          return <QuickFindsRow key={index} />;
        case 'Challenges':
          return (
            <ChallengesRow
              key={index}
              title={title}
              href={'/my-challenges'}
              index={index}
            />
          );
        case 'IncludedWorkouts':
          return (
            <IncludedWorkoutsRow
              key={index}
              title={translatedTitle}
              href={href}
              index={index}
            />
          );
        case 'Categories':
          return (
            <CategoriesRow key={index} title={translatedTitle} href={href} />
          );
        case 'Query':
          return (
            <QueryRow
              key={index}
              index={index}
              queryObj={query}
              title={title}
              translatedTitle={translatedTitle}
              href={href}
              completionsMinMax={{ min: minCompletions, max: maxCompletions }}
            />
          );
        default:
          return null;
      }
    });
  };

  if (isLoading) return <Loading absolute />;

  if (isError) {
    dispatch(showFullScreenError());
    return null;
  }
  return <>{renderRows(data?.rows)}</>;
};

export default memo(Rows);
